/**
 * Recibir un sitio de otra cuenta.
 *
 * Es la pantalla más delicada del producto: aceptar aquí convierte a quien la
 * mira en responsable de un sitio publicado, de un dominio y de una base de
 * datos personales de terceros. De ahí las decisiones que la ordenan:
 *
 *   · El inventario va ANTES que los botones, no debajo. Recibir contactos es
 *     asumir obligaciones sobre datos de gente que no eligió este cambio, y eso
 *     no puede llegar como sorpresa después de pulsar.
 *   · Rechazar es un botón secundario, no un enlace escondido. Decir que no es
 *     una respuesta legítima, y esconderla empuja a aceptar por inercia.
 *   · Los ajustes de cobros, negocio y plan se explican DESPUÉS de aceptar, en
 *     el asistente de entrega. Aquí sólo hay que entender qué sitio se recibe.
 *
 * A pantalla completa y sin el panel alrededor: quien llega aquí muchas veces
 * acaba de crear su cuenta y todavía no tiene nada suyo que enmarcar.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { longDate } from '../lib/dates'
import {
  IconAddressBook,
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowUpRight,
  IconCalendarEvent,
  IconCheck,
  IconDatabase,
  IconExternalLink,
  IconFileText,
  IconForms,
  IconLogout,
  IconPhoto,
  IconRosetteDiscountCheck,
  IconTag,
  IconWorld,
  IconX,
} from '@tabler/icons-react'
import { VelticaMascot } from '@webbuilder/ui'
import { PLAN_LIMITS } from '@webbuilder/plans'
import { acceptMyTransfer, acceptTransfer, getMyTransferOffer, getTransferOffer, publicSiteUrl, rejectMyTransfer, rejectTransfer, type TransferOffer } from '../lib/api'
import { SectionLoader, useRecordLoader } from '../components/RecordLoader'
import { useI18n, translateServerError } from '../lib/i18n'
import { DashboardTopbar } from '../components/DashboardTopbar'
import { DashboardTopbarEnd } from '../components/DashboardTopbarActions'
import { DashboardUserMenu } from '../components/DashboardUserMenu'
import { handoverHref } from '../lib/transferInvite'
import { buildPreviewSrcDoc } from '../lib/preview/render'
import type { ElementNode, PageTheme } from '../lib/preview/types'

/**
 * El ancho al que se dibuja el escritorio, en píxeles.
 *
 * Es el mismo al que se miden las plantillas del catálogo —ver
 * `templates_meta.design_height`—, así que un sitio se ve aquí como se ve allí.
 */
const CANVAS_WIDTH = 1200

type TransferAccount = {
  avatarUrl: string
  userName: string
  displayName: string
}

export function TransferAccept({ token, inviteToken, transferId, onDone, onChangeAccount, account }: {
  /** La sesión de quien recibe. */
  token: string
  /**
   * El secreto del enlace del correo. Ausente si se llega desde el panel.
   */
  inviteToken?: string | undefined
  /**
   * El id del traspaso, cuando se llega desde el panel y no desde el correo.
   *
   * Los dos caminos enseñan y deciden lo mismo; lo único que cambia es cómo se
   * encuentra la fila. El id no autoriza nada por su cuenta —el servidor sigue
   * exigiendo que el correo de la cuenta sea el de la invitación— así que la
   * pantalla puede tratarlos igual.
   */
  transferId?: string | undefined
  /** Al aceptar, rechazar o rendirse: de vuelta al panel. */
  onDone: () => void
  /** Conserva el enlace, cierra esta sesión y lleva al acceso con otra cuenta. */
  onChangeAccount?: (() => Promise<void>) | undefined
  /** La cuenta visible en la misma barra superior que usa el dashboard. */
  account: TransferAccount
}) {
  const [offer, setOffer] = useState<TransferOffer | null>(null)
  const [failed, setFailed] = useState('')
  const [busy, setBusy] = useState<'accept' | 'reject' | null>(null)
  const { t } = useI18n()
  const [changingAccount, setChangingAccount] = useState(false)
  const [wrongAccount, setWrongAccount] = useState(false)
  const [loading, setLoading] = useState(true)
  const loader = useRecordLoader(loading)

  useEffect(() => {
    let alive = true
    const ask = inviteToken
      ? getTransferOffer(token, inviteToken)
      : transferId
        ? getMyTransferOffer(token, transferId)
        : Promise.reject(new Error(t('err.noInvite')))
    ask
      .then((data) => { if (alive) setOffer(data) })
      .catch((error: unknown) => {
        /* El servidor ya explica por qué —caducada, cancelada, ya aceptada— y
           su frase es mejor que cualquiera genérica que pusiéramos aquí. */
        if (alive) {
          setWrongAccount(error instanceof Error && error.message.startsWith('403:'))
          setFailed(translateServerError(error, t('err.openInvite')))
        }
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [token, inviteToken, transferId, t])

  const decide = async (choice: 'accept' | 'reject') => {
    setBusy(choice)
    try {
      let received: string | null = null
      if (inviteToken) {
        if (choice === 'accept') received = (await acceptTransfer(token, inviteToken)).projectId
        else await rejectTransfer(token, inviteToken)
      } else if (transferId) {
        if (choice === 'accept') received = (await acceptMyTransfer(token, transferId)).projectId
        else await rejectMyTransfer(token, transferId)
      }
      /*
       * Aceptar lleva al repaso, no al panel.
       *
       * Es el único rato en que alguien tiene la cabeza puesta en este sitio y
       * en nada más. Soltarlo en el taller con un aviso arriba es pedirle que se
       * acuerde de volver a conectar sus cobros y a cambiar unos datos fiscales
       * que ni sabe que están publicados — y volver a un aviso es justo lo que
       * nadie hace. Se puede omitir entero desde allí.
       */
      if (received) window.location.replace(handoverHref(received))
      else onDone()
    } catch (error: unknown) {
      setFailed(translateServerError(error, t('err.operationFailed')))
      setBusy(null)
    }
  }

  const frame = (content: ReactNode) => (
    <div className="dash-shell dash-shell--topbar dash-shell--solo dash-transfer-shell">
      <DashboardTopbar
        mobileNavOpen={false}
        onToggleMobileNav={() => undefined}
        token={token}
        onHome={onDone}
        backToDashboard
      />
      <DashboardTopbarEnd>
        <DashboardUserMenu
          avatarUrl={account.avatarUrl}
          userName={account.userName}
          displayName={account.displayName}
          onOpenSettings={() => window.location.assign('/?view=settings')}
          onOpenPlans={() => window.location.assign('/?view=plans')}
          onOpenUsage={() => window.location.assign('/?view=usage')}
          onOpenBilling={() => window.location.assign('/?view=billing')}
          onOpenDomains={() => window.location.assign('/?view=domains')}
        />
      </DashboardTopbarEnd>
      <main className="dash-main dash-transfer-main">{content}</main>
    </div>
  )

  if (loader.visible) {
    return frame(
      <div className="dash-transfer-loading">
        <SectionLoader loader={loader} label={t('transfer.loading')} />
      </div>,
    )
  }

  if (failed || !offer) {
    return frame(
      <div className="dash-transfer-screen">
        <div className="dash-placeholder dash-loaded-content-in">
          <IconAlertTriangle size={34} aria-hidden="true" />
          <strong>{t(wrongAccount ? 'transfer.wrongAccount' : 'transfer.unavailable')}</strong>
          <span>{failed || t('transfer.unavailableBody')}</span>
          <div className="dash-transfer-actions">
            <button type="button" className="dash-secondary-btn" onClick={onDone}>{t('transfer.toPanel')}</button>
            {wrongAccount && onChangeAccount ? (
              <button
                type="button"
                className="dash-primary-btn"
                disabled={changingAccount}
                onClick={() => {
                  setChangingAccount(true)
                  void onChangeAccount().catch(() => {
                    setChangingAccount(false)
                    setFailed(t('transfer.signOutFailed'))
                  })
                }}
              >
                <IconLogout size={15} aria-hidden="true" />
                {changingAccount ? 'Cambiando…' : 'Cambiar de cuenta'}
              </button>
            ) : null}
          </div>
        </div>
      </div>,
    )
  }

  const stock = offer.inventory
  /*
   * Una sola cifra por ficha, y lo demás como pie.
   *
   * Antes iban dos números pegados con un punto —«2 · 25 mensajes»— y no se
   * entendía cuál contaba qué: son formularios PUESTOS en el sitio y respuestas
   * RECIBIDAS en ellos, dos cosas distintas metidas en un renglón. La cifra
   * grande es siempre lo que da nombre a la ficha; el pie matiza.
   *
   * El peso de las imágenes se fue del todo: en megas no se decide nada —quien
   * recibe no paga por almacenamiento— y competía con el número que sí importa.
   */
  /* Las dos formas por clave: el singular y el plural no se arman pegando una
     «s», y en otros idiomas ni siquiera son dos. */
  const plural = (n: number, one: string, many: string) => t(n === 1 ? one : many, { count: n })
  const planLimits = PLAN_LIMITS[offer.plan]
  const exceeds = (value: number, limit: number | null) => limit !== null && value > limit

  const items: { icon: typeof IconFileText, label: string, value: string, note?: string, text?: boolean, overLimit?: boolean }[] = [
    { icon: IconFileText, label: t('transfer.stock.pages'), value: String(stock.pages), overLimit: exceeds(stock.pages, planLimits.pages) },
    {
      icon: IconDatabase,
      label: t('transfer.stock.collections'),
      value: String(stock.collections),
      overLimit: exceeds(stock.collections, planLimits.collections) || exceeds(stock.cmsItems, planLimits.cmsItems),
      /* Las entradas como pie y no como ficha propia: la colección es el molde y
         las entradas son lo que hay dentro, así que separarlas en dos cifras del
         mismo tamaño haría pensar que son dos cosas distintas que se reciben. */
      ...(stock.cmsItems > 0 ? { note: plural(stock.cmsItems, 'transfer.stock.entriesOne', 'transfer.stock.entriesMany') } : {}),
    },
    { icon: IconAddressBook, label: t('transfer.stock.contacts'), value: String(stock.contacts), overLimit: exceeds(stock.contacts, planLimits.contacts) },
    {
      icon: IconForms,
      label: t('transfer.stock.forms'),
      value: String(stock.forms),
      note: plural(stock.messages, 'transfer.stock.messagesOne', 'transfer.stock.messagesMany'),
    },
    { icon: IconPhoto, label: t('transfer.stock.images'), value: String(stock.images) },
    /* El catálogo y lo vendido, sólo si existen. Un sitio que no vende nada no
       tiene por qué enseñar dos ceros de comercio: llenan la rejilla y no dicen
       nada. Los demás siempre van, porque un cero ahí sí informa —«no hay
       contactos» es un dato al recibir un sitio—. */
    ...(stock.offers > 0
      ? [{
          icon: IconTag,
          label: t('transfer.stock.offers'),
          value: String(stock.offers),
          ...(stock.orders > 0 ? { note: plural(stock.orders, 'transfer.stock.salesOne', 'transfer.stock.salesMany') } : {}),
        }]
      : []),
    ...(stock.upcomingBookings > 0
      ? [{
          icon: IconCalendarEvent,
          label: t('transfer.stock.bookings'),
          value: String(stock.upcomingBookings),
          note: t('transfer.stock.bookingsNote'),
        }]
      : []),
    /* El dominio no es una cantidad, así que va en letra de texto y no de cifra:
       con el tamaño de los números, un nombre de dominio se sale de la ficha. */
    ...(stock.customDomain ? [{ icon: IconWorld, label: t('transfer.stock.domain'), value: stock.customDomain, text: true }] : []),
  ]

  return frame(
    <div className="dash-transfer-page dash-loaded-content-in">
      {/* A dos columnas, como el acceso: a la izquierda la decisión, a la derecha
          el sitio del que se está hablando. En una sola columna el inventario
          eran seis cifras sobre un objeto que quien recibe nunca ha visto —una
          entrega de agencia llega muchas veces a alguien que no siguió el
          trabajo—, y «10 páginas» no dice nada si no se sabe de qué. */}
      <section className="dash-transfer-panel">
        <div className="dash-transfer-panel-inner">
          <header className="dash-transfer-head">
            {/* La mascota recibe, igual que en la pantalla sin proyectos. Aquí
                hace más falta que en ninguna otra: mucha gente llega por un
                correo, con la cuenta recién hecha y sin panel alrededor que diga
                dónde está. */}
            <VelticaMascot className="dash-transfer-mascot" size={58} />
            <p>{t('transfer.kicker')}</p>
            <h1>{offer.projectName}</h1>
            <span>{t('transfer.lead')}</span>
          </header>

          <section className="dash-transfer-card" aria-label={t('transfer.includedAria')}>
            <h2>{t('transfer.included')}</h2>
            <ul className="dash-transfer-stock">
              {items.map((item) => {
                const Icon = item.icon
                return (
                  <li
                    key={item.label}
                    className={`${item.note || item.text ? 'has-detail' : ''}${item.overLimit ? ' is-over-limit' : ''}`.trim() || undefined}
                  >
                    <span className="dash-widget-glass-icon dash-sidebar-glass-icon is-fog" aria-hidden="true"><Icon size={15} /></span>
                    <strong className={item.text ? 'is-text' : undefined}>{item.value}</strong>
                    <span className="dash-transfer-stock-label">
                      <span>{item.label}</span>
                      {item.overLimit ? (
                        <span className="dash-transfer-over-limit-marker" title={t('transfer.overLimit')}>
                        <IconAlertCircle size={14} aria-hidden="true" />
                        <span className="dash-sr-only">{t('transfer.overLimit')}</span>
                        </span>
                      ) : null}
                    </span>
                    {item.note ? <small>{item.note}</small> : null}
                  </li>
                )
              })}
            </ul>
          </section>

          {!offer.planFits ? (
            <aside className="dash-transfer-membership-note" aria-label={t('transfer.membershipAria')}>
              <span className="dash-widget-glass-icon dash-sidebar-glass-icon is-violet" aria-hidden="true">
                <IconRosetteDiscountCheck size={15} />
              </span>
              <div>
                <strong>{t('transfer.needsMembership')}</strong>
                <p>{t('transfer.needsMembershipBody')}</p>
                <a href="/?view=plans" target="_blank" rel="noreferrer">
                  {t('transfer.seePlans')} <IconArrowUpRight size={13} aria-hidden="true" />
                </a>
              </div>
            </aside>
          ) : null}

          <div className="dash-transfer-actions">
            <button type="button" className="dash-secondary-btn" disabled={busy !== null} onClick={() => void decide('reject')}>
              <IconX size={14} /> {t(busy === 'reject' ? 'transfer.rejecting' : 'transfer.reject')}
            </button>
            <button type="button" className="dash-primary-btn" disabled={busy !== null} onClick={() => void decide('accept')}>
              <IconCheck size={14} /> {t(busy === 'accept' ? 'transfer.accepting' : 'transfer.accept')}
            </button>
          </div>

          <p className="dash-transfer-expiry">{t('transfer.expiresOn', { date: longDate(offer.expiresAt) })}</p>
        </div>
      </section>

      <SiteGlance
        cover={offer.cover}
        subdomain={stock.subdomain}
        published={stock.published}
        customDomain={stock.customDomain}
      />
    </div>,
  )
}

/**
 * El sitio del que va todo esto, a la derecha.
 *
 * ── Por qué NO es el sitio publicado ────────────────────────────────────────
 *
 * Lo era, y estaba mal: un proyecto sin publicar viaja entero igual —con sus
 * páginas, su contenido y su dominio— y quien lo recibe tiene exactamente el
 * mismo derecho a verlo antes de decidir. Justo el caso de una agencia que
 * entrega un sitio terminado y todavía no puesto en línea, que es de los más
 * comunes, se quedaba con un cartel diciendo que no había nada que mirar.
 *
 * Así que se dibuja la portada desde su contenido, con el mismo camino que la
 * vista previa de una copia de seguridad: el panel ya sabe convertir un árbol de
 * elementos y un tema en una página. Va en `srcDoc` y no en una URL: no hay
 * objeto que revocar después y el marco queda aislado de la sesión del panel.
 *
 * Es el renderizador de vista previa, no el de publicación —el HTML que se
 * publica lo genera el editor—, así que puede haber diferencias de detalle. Para
 * lo que esta pantalla necesita, que es reconocer el sitio, sobra.
 *
 * Sin interacción a propósito: es una vista, no una sesión de navegación. Si
 * está publicado, el enlace de debajo abre el sitio de verdad en otra pestaña.
 */
function SiteGlance({ cover, subdomain, published, customDomain }: {
  cover: TransferOffer['cover']
  subdomain: string | null
  published: boolean
  customDomain: string | null
}) {
  const { t } = useI18n()
  const url = published && subdomain ? publicSiteUrl(subdomain) : null

  /* El documento se arma una vez: es una cadena de HTML entera, y rehacerla en
     cada repintado remontaría el marco y lo dejaría parpadeando. */
  const srcDoc = useMemo(() => {
    if (!cover) return null
    return buildPreviewSrcDoc(cover.elements as ElementNode[], (cover.theme ?? undefined) as PageTheme | undefined, 'desktop')
  }, [cover])

  /* El documento puede desplazarse, pero sigue siendo una vista: desactivar
     sólo los controles conserva la rueda y el gesto táctil sobre la página sin
     convertir un clic accidental en navegación dentro del marco. */
  const scrollableSrcDoc = useMemo(() => {
    if (!srcDoc) return null
    const inertControls = '<style data-transfer-preview>a,button,input,select,textarea,label,[role="button"]{pointer-events:none!important}</style>'
    return srcDoc.includes('</head>')
      ? srcDoc.replace('</head>', `${inertControls}</head>`)
      : `${inertControls}${srcDoc}`
  }, [srcDoc])

  /*
   * La escala se MIDE, no se supone.
   *
   * El sitio tiene que dibujarse al ancho de escritorio y reducirse después: si
   * se le diera el ancho real de esta columna, respondería a él y se pintaría en
   * su versión de móvil, que no es lo que se está entregando ni lo que nadie
   * reconocería. La reducción es lo que hace que se vea COMO SE VE.
   *
   * Estaba clavada a un factor fijo, y por eso no cuadraba: la columna es un
   * porcentaje de la ventana, así que su ancho cambia con cada tamaño de
   * pantalla y con cada barra lateral del navegador. Con un `ResizeObserver` el
   * marco se entera de su propio ancho y la escala sale de una división.
   */
  const frame = useRef<HTMLDivElement | null>(null)
  const [box, setBox] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const node = frame.current
    if (!node) return
    const observer = new ResizeObserver(([entry]) => {
      const rect = entry?.contentRect
      if (rect) setBox({ width: rect.width, height: rect.height })
    })
    observer.observe(node)
    return () => { observer.disconnect() }
  }, [srcDoc])

  const scale = box.width > 0 ? box.width / CANVAS_WIDTH : 0

  return (
    <section className="dash-transfer-preview" aria-label={t('transfer.previewAria')}>
      <div className="dash-transfer-preview-frame" ref={frame}>
        {scrollableSrcDoc ? (
          /* Hasta que hay medida no se pinta: con escala cero el marco saldría
             del tamaño de un punto y se vería dar el salto al medirse. */
          scale > 0 ? (
            <iframe
              title={t('transfer.previewAria')}
              className="dash-transfer-preview-iframe"
              /* `allow-same-origin` y nada más, igual que la vista previa de una
                 copia: las fuentes y los estilos del documento lo necesitan, pero
                 no hay scripts, ni formularios, ni navegación posible. */
              sandbox="allow-same-origin"
              srcDoc={scrollableSrcDoc}
              style={{
                width: CANVAS_WIDTH,
                /* El alto, deshecha la reducción: así el documento ocupa
                   exactamente el hueco visible una vez encogido, ni deja franja
                   en blanco abajo ni se corta antes de tiempo. */
                height: box.height / scale,
                transform: `scale(${scale})`,
              }}
            />
          ) : null
        ) : (
          /* Un proyecto sin ninguna página con contenido. Ahí sí no hay nada que
             dibujar, y un marco en blanco se leería como que algo falló. */
          <div className="dash-transfer-preview-empty">
            <VelticaMascot size={52} />
            <strong>{t('transfer.nothingDrawn')}</strong>
            <span>{t('transfer.nothingDrawnBody')}</span>
          </div>
        )}
      </div>

      <p className="dash-transfer-preview-foot">
        {cover ? <span className="dash-transfer-preview-page">{cover.name}</span> : null}
        {url ? (
          <a className="dash-transfer-preview-link" href={url} target="_blank" rel="noreferrer">
            <IconExternalLink size={14} aria-hidden="true" />
            {customDomain ?? url.replace(/^https?:\/\//, '')}
          </a>
        ) : (
          /* Sin publicar no es un fallo, es un dato de la decisión: se recibe un
             sitio terminado pero no puesto en línea, no un negocio en marcha. */
          <span className="dash-transfer-preview-offline">{t('transfer.notPublished')}</span>
        )}
      </p>
    </section>
  )
}
